import * as React from "react";
import {
  SingleNavMenuLink,
  toLinkButton,
  toLinkDivider,
  toLinkExternal,
  toLinkPage,
  toLinkSpace,
} from "../identity/useNavMenu";
import { renderEventWarningIcon } from "../identity/navMenuIcons";
import { formSet, formSetValue, useCpFormAnyValueId, useCpFormModifiedAnyId, useCpFormValue } from "../gvc/testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { BasePathContext, useBasePath } from "../../reactContexts/basePathContext";
import { useAppDispatch } from "../../store/hooks";
import { IS_DEPLOYMENT_ENV_PROD, IS_LOCAL } from "../../envVariables";
import { ContentLayout } from "../../components/layout/contentLayout";
import { SingleNavMenu } from "../../components/antd/layout/singleNavMenu";
import { CpTagsNew } from "../../components/form/CpTagsNew";
import { useEventlogs } from "../../reactHooks/useEventlog";
import { linksOf, request } from "../../services/cpln";
import { formActions } from "../../store/formSlice";
import { Events } from "../../components/detail/events";
import { AccessReportPage } from "../../components/detail/accessReport";
import { AuditTrail } from "../../components/detail/auditTrail";
import { Summary } from "./summary";
import { GvcDetailHeader } from "./header";
import { Gvc } from "../../schema/types/gvc/entity";
import { store } from "../../store";
import { GvcInput } from "../../schema/types/gvc/input";
import { cloneDeep } from "lodash";
import { EndpointNamingFormat } from "./create/endpointNamingFormat";
import { LocationItems } from "./locationItems";
import { LocationOptions } from "./locationOptions";
import { LocationQuery } from "./locationQuery";
import { PullSecretItems } from "./pullSecretItems";
import { GvcEnvVars } from "./envVars/envVars";
import { LoadBalancer } from "./loadBalancer/loadBalancer";
import { Keda } from "./keda";
import { Tracing } from "./tracing";
import { GvcSidecarEnvoy } from "./sidecarEnvoy";
import { Metrics } from "../metrics/metrics";
import { CpRootError } from "./testNewForm/CpRootError";

interface Props {
  gvc: Gvc;
  refetch?: () => void;
}
export const GvcDetailContent: React.FC<Props> = ({ gvc, refetch }) => {
  const dispatch = useAppDispatch();
  const basePath = useBasePath(`/gvc/:gvc/*`);
  const { pathname } = useLocation();

  // eventlog
  const eventlogHook = useEventlogs(linksOf(gvc).self!, refetch);
  React.useEffect(() => {
    eventlogHook.setItemLastModified(gvc.lastModified);
  }, [gvc.lastModified]);
  React.useEffect(() => {
    if (pathname.includes("/events")) {
      return;
    }

    const id = setInterval(() => eventlogHook.fetchEventlogs(true), 30 * 1000);

    return () => {
      if (id) {
        clearTimeout(id);
      }
    };
  }, [pathname]);

  const { id: formId, submit } = useCpFormContext();

  const submitting = useCpFormAnyValueId(formId, `meta.submitting`);
  const anyModified = useCpFormModifiedAnyId(formId);
  const formVersion = useCpFormValue(`version`);

  React.useEffect(() => {
    if (!formVersion) return;
    // Fetch items which were saved as links, applies to whole website
    fetchLocationItems();
    fetchPullSecretItems();
  }, [formVersion]);

  async function fetchLocationItems() {
    const form = store.getState().form.instances[formId];
    const values: GvcInput = form.values;

    const locationItems = cloneDeep(values.locationItems);

    for (const li of locationItems) {
      const link = li.link;
      const { data } = await request({ url: link });
      li.item = data;
    }
    formSetValue(formId, `locationItems`, locationItems);
  }

  async function fetchPullSecretItems() {
    const form = store.getState().form.instances[formId];
    const values: GvcInput = form.values;

    const pullSecretItems = cloneDeep(values.pullSecretItems);

    for (const li of pullSecretItems) {
      const link = li.link;
      const { data } = await request({ url: link });
      li.item = data;
    }
    formSetValue(formId, `pullSecretItems`, pullSecretItems);
  }

  const links = React.useMemo(() => {
    const links: SingleNavMenuLink[] = [];
    links.push(
      //
      toLinkPage("Summary", "/summary", { names: ["description"], showDirty: true }),
      toLinkPage("Endpoint Naming", "/endpoint-naming-format", { names: ["endpointNamingFormat"], showDirty: true }),
      toLinkPage("Locations", "/locations", { names: ["locationItems"], showDirty: true }),
      toLinkPage("Location Query", "/location-query", { names: ["locationQuery"], showDirty: true }),
      toLinkPage("Location Options", "/location-options", { names: ["locationOptions"], showDirty: true }),
      toLinkPage("Pull Secrets", "/pull-secrets", { names: ["pullSecretItems"], showDirty: true }),
      toLinkPage("Env Variables", "/environment-variables", { names: ["env"], showDirty: true }),
      toLinkPage("Load Balancer", "/load-balancer", { names: ["loadBalancer"], showDirty: true }),
      toLinkPage("Keda", "/keda", { names: ["keda"], showDirty: true }),
      toLinkPage("Tracing", "/tracing", { names: ["tracing"], showDirty: true }),
      toLinkPage("Sidecar | Envoy", "/sidecar-envoy", { names: ["sidecar"], showDirty: true }),
      toLinkPage("Tags", "/tags", { names: ["tags"], showDirty: true }),
      toLinkDivider(),
    );
    links.push(toLinkPage("Metrics", "/metrics"));
    // toLinkExternal(`Metrics`, `metrics-gvc`),
    if (gvc.spec.tracing?.provider?.controlplane) {
      links.push(toLinkExternal(`Traces`, `traces-gvc`));
    }
    links.push(
      ...[
        toLinkDivider(),
        toLinkPage("Events", "/events", { renderIcon: renderEventWarningIcon(eventlogHook.hasWarning) }),
        toLinkPage("Audit Trail", "/audit-trail"),
        toLinkPage("Access Report", "/access-report"),
      ],
    );

    if (anyModified) {
      links.push(
        toLinkSpace(),
        toLinkButton("Reset", {
          onClick: () => {
            dispatch(formActions.reset(formId));
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Preview", {
          onClick: () => {
            formSet(formId, `meta.previewing`, true);
          },
          type: "default",
          disabled: submitting,
        }),
        toLinkButton("Update", {
          onClick: async () => {
            await submit();
          },
          type: "primary",
          className: "action",
          disabled: submitting,
          loading: submitting,
        }),
      );
    }

    if (IS_LOCAL || !IS_DEPLOYMENT_ENV_PROD) {
      if (!anyModified) {
        links.push(toLinkSpace());
      }
      links.push(
        toLinkButton("Debug", {
          type: "default",
          onClick: () => formSet(formId, `meta.debugging`, true),
          disabled: submitting,
        }),
      );
    }

    return links;
  }, [anyModified, submitting, eventlogHook.hasWarning]);

  return (
    <BasePathContext.Provider value={basePath}>
      <GvcDetailHeader gvc={gvc} />
      <ContentLayout
        nav={<SingleNavMenu useDetailHeader={true} links={links} />}
        content={
          <div className="cpln-cards-container">
            <CpRootError />
            <Routes>
              <Route path={`summary/*`} element={<Summary gvc={gvc} />} />
              <Route path={`endpoint-naming-format/*`} element={<EndpointNamingFormat />} />
              <Route path={`locations/*`} element={<LocationItems />} />
              <Route path={`location-options/*`} element={<LocationOptions />} />
              <Route path={`location-query/*`} element={<LocationQuery />} />
              <Route path={`pull-secrets/*`} element={<PullSecretItems />} />
              <Route path={`environment-variables/*`} element={<GvcEnvVars />} />
              <Route path={`load-balancer/*`} element={<LoadBalancer />} />
              <Route path={`keda/*`} element={<Keda />} />
              <Route path={`tracing/*`} element={<Tracing />} />
              <Route path={`sidecar-envoy/*`} element={<GvcSidecarEnvoy />} />
              <Route path={`tags/*`} element={<CpTagsNew />} />
              <Route path={`metrics/*`} element={<Metrics gvc={[gvc.name]} />} />
              <Route
                path={`events`}
                element={
                  <Events
                    kind={"gvc"}
                    eventlogs={eventlogHook.eventlogs}
                    isLoading={eventlogHook.isLoading}
                    fetchEventlogs={eventlogHook.fetchEventlogs}
                    isFetching={eventlogHook.isFetching}
                    nextLink={eventlogHook.nextLink}
                  />
                }
              />
              <Route
                path={`audit-trail`}
                element={
                  <AuditTrail
                    kind={"gvc"}
                    itemName={gvc.name}
                    resourceId={gvc.id}
                    version={gvc.version}
                    selfLink={linksOf(gvc).self!}
                    refetch={refetch}
                  />
                }
              />
              <Route path={`access-report`} element={<AccessReportPage itemLink={linksOf(gvc).self!} />} />
              <Route path={`*`} element={<Navigate to={`${basePath}/summary`} replace />} />
            </Routes>
          </div>
        }
      />
    </BasePathContext.Provider>
  );
};
